import React, { useMemo, useState } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Tag,
  Input,
  Alert,
  Spin,
  message,
} from "antd";
import {
  ApiOutlined,
  SendOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { supabase } from "../../../lib/supabase";
import { trackFeatureUsage } from "../../../lib/posthog";
import {
  buildBotBookingPayload,
  type BookingInsight,
  type LookupResult,
} from "./botAdminShared";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface BotReplyStepProps {
  selectedResult: LookupResult | null;
  selectedCompanyId: number | null;
  bookingInsight: BookingInsight | null;
  onBack: () => void;
}

export const BotReplyStep: React.FC<BotReplyStepProps> = ({
  selectedResult,
  selectedCompanyId,
  bookingInsight,
  onBack,
}) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [todayLabel, setTodayLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const bookingPayload = useMemo(() => {
    if (!bookingInsight || selectedCompanyId == null) return null;
    return buildBotBookingPayload(bookingInsight, selectedCompanyId);
  }, [bookingInsight, selectedCompanyId]);

  const companyPayload = useMemo(() => {
    if (!selectedResult) return null;
    const { company, users } = selectedResult;
    return {
      id: company.id,
      name: company.name,
      status: company.status ?? null,
      phone_number: company.phone_number ?? null,
      invoice_email: company.invoice_email ?? null,
      city: company.city ?? null,
      country: company.country ?? null,
      users: users.map((u) => ({ id: u.id, email: u.email })),
    };
  }, [selectedResult]);

  const canAsk =
    selectedCompanyId != null &&
    !!companyPayload &&
    !!bookingPayload &&
    question.trim().length > 0;

  const handleAsk = async () => {
    if (!canAsk || !companyPayload || !bookingPayload) {
      if (!bookingPayload) {
        message.warning("Look up a booking in step 3 first.");
      } else if (!companyPayload) {
        message.warning("Select a company in step 2 first.");
      }
      return;
    }

    setLoading(true);
    setAnswer(null);
    setModel(null);
    setTodayLabel(null);
    trackFeatureUsage("admin_bot_reply");

    try {
      const { data, error } = await supabase.functions.invoke("bot-reply", {
        body: {
          question: question.trim(),
          company: companyPayload,
          booking: bookingPayload,
        },
      });

      if (error) throw error;

      const payload = data as {
        answer?: string;
        model?: string;
        error?: string;
        today?: {
          today_ymd?: string;
          today_weekday?: string;
          timezone?: string;
        };
      } | null;

      if (payload?.error) {
        throw new Error(payload.error);
      }
      if (!payload?.answer) {
        throw new Error("No answer returned");
      }

      setAnswer(payload.answer);
      setModel(payload.model ?? null);
      if (payload.today?.today_ymd) {
        setTodayLabel(
          `${payload.today.today_weekday ?? ""}, ${payload.today.today_ymd} (${
            payload.today.timezone ?? "Europe/Amsterdam"
          })`.trim(),
        );
      }
    } catch (err) {
      console.error("Bot reply failed:", err);
      message.error(
        err instanceof Error ? err.message : "Failed to get bot reply",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <ApiOutlined />
          <span>Step 4 — Bot reply</span>
          <Tag color="blue">OpenAI preview</Tag>
          <Tag icon={<LockOutlined />}>Admin only</Tag>
        </Space>
      }
      extra={
        <Button type="link" onClick={onBack}>
          ← Back
        </Button>
      }
      style={{ marginBottom: 24 }}
    >
      {!companyPayload && (
        <Alert
          type="warning"
          showIcon
          message="Select a company in step 2 first"
          style={{ marginBottom: 16 }}
        />
      )}
      {companyPayload && !bookingPayload && (
        <Alert
          type="warning"
          showIcon
          message="Look up a booking in step 3 first"
          description="The reply uses company + booking JSON as context."
          style={{ marginBottom: 16 }}
        />
      )}
      {companyPayload && bookingPayload && (
        <Alert
          type="info"
          showIcon
          message={`Context: ${companyPayload.name} · ${
            String(bookingPayload.eff_reference ?? bookingPayload.id)
          }`}
          description="OpenAI answers only from this company + booking payload (same JSON as step 3)."
          style={{ marginBottom: 16 }}
        />
      )}

      <Paragraph type="secondary">
        Ask a simple customer-style question. Example: “Where is my shipment?”
        or “Wat is de ETA?”
      </Paragraph>

      <TextArea
        rows={3}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="e.g. Where is booking EFF7756SEA and when will it arrive?"
        disabled={loading || !companyPayload || !bookingPayload}
        maxLength={2000}
        showCount
        style={{ marginBottom: 12 }}
      />

      <Button
        type="primary"
        icon={<SendOutlined />}
        loading={loading}
        disabled={!canAsk}
        onClick={handleAsk}
      >
        Ask bot
      </Button>

      {loading && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin tip="Thinking…" />
        </div>
      )}

      {!loading && answer && (
        <Card
          size="small"
          type="inner"
          style={{ marginTop: 16 }}
          title={
            <Space>
              <Text strong>Reply</Text>
              {model && <Tag>{model}</Tag>}
              {todayLabel && <Tag color="blue">Today: {todayLabel}</Tag>}
            </Space>
          }
        >
          <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
            {answer}
          </Paragraph>
        </Card>
      )}
    </Card>
  );
};
